import { Loader2, Search } from "lucide-react";
import { useChatStore } from "@/store/chatStore";
import { Avatar } from "@/components/ui/chat-avatar";
import { Badge } from "@/components/ui/chat-badge";
import { NetworkStatus } from "@/components/ui/network-status";
import { MobileSidebar } from "@/components/MobileSidebar";
import { formatTime } from "@/libs/utils";
import { cn } from "@/libs/utils";
import { useRoomsInfinite } from "@/hooks/use-queries";
import { Room } from "@/services/apiService";
import { useEffect } from "react";

export function ChatList() {
  const {
    selectedRoomId,
    searchQuery,
    setSelectedRoom,
    setSearchQuery,
    setRooms,
    setSelectedRoomSlug,
  } = useChatStore();

  const handleSelectRoom = (roomId: number, roomSlug: string) => {
    setSelectedRoom(roomId);
    setSelectedRoomSlug(roomSlug);
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useRoomsInfinite();

  const rooms = data?.pages.flatMap((page) => page.results).flat() ?? [];

  useEffect(() => {
    if (!isLoading && !!rooms) {
      setRooms(rooms);
    }
  }, [rooms?.length, isLoading]);

  const filteredRooms = rooms.filter((room: { name: string }) => {
    return room.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="flex flex-col h-full bg-background border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MobileSidebar />
            <h1 className="text-xl font-bold">Messages</h1>
          </div>
          <NetworkStatus />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full pl-10 pr-4 py-2 rounded-lg",
              "bg-background",
              "border border-border",
              "text-foreground",
              "placeholder:text-muted-foreground",
              "focus:outline-none focus:ring-2 focus:ring-[#D8902F]",
              "hover:border-[#D8902F]"
            )}
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {filteredRooms.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {!!isLoading
                  ? "Loading rooms.."
                  : rooms.length === 0
                  ? "No rooms found"
                  : ""}
              </p>
            </div>
          </div>
        ) : (
          filteredRooms.map((room) => (
            <ChatListItem
              key={room.id}
              room={room}
              isSelected={room.id === selectedRoomId}
              onClick={() => handleSelectRoom(room.id, room.slug)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ChatListItem({
  room,
  isSelected,
  onClick,
}: {
  room: Room;
  isSelected: boolean;
  onClick: () => void;
}) {
  const roomName = room.name;

  const roomAvatar = room.image;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-4",
        "hover:bg-muted",
        "transition-colors duration-150",
        "border-b border-border",
        isSelected && "bg-[var(--chat-selected)]"
      )}
    >
      <Avatar src={roomAvatar} alt={roomName!} />

      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium truncate">{roomName}</h3>
        </div>
      </div>
    </button>
  );
}
